/** Constantes y helpers para resumen / gráficos de órdenes de compra. */

export const PO_SUMMARY_METRIC_OPTIONS = [
  { value: 'count', label: 'Cantidad de órdenes' },
  { value: 'amount', label: 'Montos de órdenes' },
];

export const PO_STATUS_ORDER = [
  'Borrador',
  'Respaldo',
  'Emitida',
  'Pendiente Aprobación',
  'Aprobada',
  'Enviada',
  'Para Reserva',
  'Recepción Conforme',
  'Recepción No Conforme',
  'Por Pagar',
  'Por Pagar Atrasado',
  'Pagado Atrasado',
  'Pagado a Tiempo',
  'Anulada',
];

export const PO_STATUS_COLORS = {
  Borrador: '#9ca3af',
  Respaldo: '#64748b',
  Emitida: '#0ea5e9',
  'Pendiente Aprobación': '#7c3aed',
  Aprobada: '#16a34a',
  Enviada: '#2563eb',
  'Para Reserva': '#0891b2',
  'Recepción Conforme': '#0d9488',
  'Recepción No Conforme': '#dc2626',
  'Por Pagar': '#f59e0b',
  'Por Pagar Atrasado': '#ea580c',
  'Pagado Atrasado': '#b45309',
  'Pagado a Tiempo': '#15803d',
  Anulada: '#ef4444',
};

const foldStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return `rgba(100, 116, 139, ${alpha})`;
  const n = Number.parseInt(raw, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const PO_ITEM_TYPE_ORDER = [
  'Materias primas',
  'Insumos',
  'Upselling',
  'Servicios',
  'Transporte',
  'Otros',
];

export const PO_ITEM_TYPE_COLORS = {
  'Materias primas': '#2563eb',
  Insumos: '#0d9488',
  Upselling: '#db2777',
  Servicios: '#7c3aed',
  Transporte: '#0891b2',
  Otros: '#64748b',
};

export const PO_STATUS_STACK_DIMENSION = {
  key: 'status',
  label: 'Estado',
  order: PO_STATUS_ORDER,
  colors: PO_STATUS_COLORS,
  fallback: 'Sin estado',
};

export const PO_ITEM_TYPE_STACK_DIMENSION = {
  key: 'itemType',
  label: 'Tipo de compra',
  order: PO_ITEM_TYPE_ORDER,
  colors: PO_ITEM_TYPE_COLORS,
  fallback: 'Sin tipo',
};

export const getPoAnchorDate = (order) => order?.order_date || order?.created_at || '';

export const normalizePoStatus = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Sin estado';
  const folded = foldStatus(raw);
  const found = PO_STATUS_ORDER.find((status) => foldStatus(status) === folded);
  if (found) return found;
  if (folded.includes('pendiente') && folded.includes('aprobacion')) return 'Pendiente Aprobación';
  if (folded === 'emitida' || folded === 'emitido') return 'Emitida';
  if (folded === 'recibida' || folded === 'parcial') return 'Recepción Conforme';
  if (folded === 'por facturar') return 'Por Pagar';
  if (folded === 'confirmada' || folded === 'registrada') return 'Enviada';
  return raw;
};

export const isPendingPoApprovalStatus = (value) => {
  const folded = foldStatus(value);
  return (
    (folded.includes('pendiente') && folded.includes('aprobacion')) ||
    folded === 'por aprobar'
  );
};

export const canRequestPoApproval = (value) => {
  const folded = foldStatus(value);
  return folded === 'borrador';
};

/** OC en ciclo de pago (por pagar o ya pagadas). */
export const isPoPaymentLifecycleStatus = (value) => {
  const normalized = normalizePoStatus(value);
  return (
    normalized === 'Por Pagar' ||
    normalized === 'Por Pagar Atrasado' ||
    normalized === 'Pagado Atrasado' ||
    normalized === 'Pagado a Tiempo'
  );
};

export const isPoAwaitingPaymentStatus = (value) => {
  const normalized = normalizePoStatus(value);
  return normalized === 'Por Pagar' || normalized === 'Por Pagar Atrasado';
};

export const isPoPaidStatus = (value) => {
  const normalized = normalizePoStatus(value);
  return normalized === 'Pagado Atrasado' || normalized === 'Pagado a Tiempo';
};

/** Estado de flujo de la OC (columna Estado). */
export const PO_WORKFLOW_STATUS_ORDER = [
  'Borrador',
  'Por Aprobar',
  'Aprobada',
  'Emitida',
  'Anulada',
];

export const PO_WORKFLOW_STATUS_COLORS = {
  Borrador: '#9ca3af',
  'Por Aprobar': '#7c3aed',
  Aprobada: '#16a34a',
  Emitida: '#0ea5e9',
  Anulada: '#ef4444',
};

export const PO_INVOICING_STATUS_ORDER = ['Sin Facturar', 'Facturado Parcial', 'Facturado'];

export const PO_INVOICING_STATUS_COLORS = {
  'Sin Facturar': '#94a3b8',
  'Facturado Parcial': '#f59e0b',
  Facturado: '#2563eb',
};

export const PO_RECEPTION_STATUS_ORDER = [
  'No Aplica',
  'Sin Recepción',
  'Recepción Parcial',
  'Recepción con Incidente',
  'Recepción Conforme',
];

export const PO_RECEPTION_STATUS_COLORS = {
  'No Aplica': '#94a3b8',
  'Sin Recepción': '#64748b',
  'Recepción Parcial': '#d97706',
  'Recepción con Incidente': '#dc2626',
  'Recepción Conforme': '#0d9488',
};

export const PO_PAYMENT_STATUS_ORDER = ['Por Pagar', 'Vencido', 'Pago Parcial', 'Pago Completo'];

export const PO_PAYMENT_STATUS_COLORS = {
  'Por Pagar': '#f59e0b',
  Vencido: '#ea580c',
  'Pago Parcial': '#0891b2',
  'Pago Completo': '#15803d',
};

const presentationFromMaps = (label, colors, isDarkMode = false) => {
  const color = colors[label] || (isDarkMode ? '#71717a' : '#a8a29e');
  return {
    label: label || '—',
    color,
    bar: color,
    bg: hexToRgba(color, isDarkMode ? 0.22 : 0.12),
    border: hexToRgba(color, isDarkMode ? 0.55 : 0.35),
  };
};

export const resolvePoWorkflowStatus = (orderOrStatus) => {
  const raw =
    orderOrStatus && typeof orderOrStatus === 'object'
      ? orderOrStatus.status
      : orderOrStatus;
  const normalized = normalizePoStatus(raw);
  if (normalized === 'Anulada') return 'Anulada';
  if (normalized === 'Borrador' || normalized === 'Respaldo') return 'Borrador';
  if (normalized === 'Pendiente Aprobación' || isPendingPoApprovalStatus(raw)) return 'Por Aprobar';
  if (normalized === 'Emitida') return 'Emitida';
  if (normalized === 'Aprobada') return 'Aprobada';
  // Etapas posteriores al flujo de aprobación se muestran como Aprobada.
  if (
    [
      'Enviada',
      'Para Reserva',
      'Recepción Conforme',
      'Recepción No Conforme',
      'Por Pagar',
      'Por Pagar Atrasado',
      'Pagado Atrasado',
      'Pagado a Tiempo',
    ].includes(normalized)
  ) {
    return 'Aprobada';
  }
  return normalized === 'Sin estado' ? 'Borrador' : 'Borrador';
};

export const resolvePoInvoicingStatus = (order = {}) => {
  const itemsCount = Number(order.items_count || 0);
  const invoiced = Number(order.invoiced_items_count || 0);
  const hasFolioHint = Boolean(String(order.invoice_folios || '').trim() || order.invoice_issue_date);
  if (itemsCount <= 0) {
    return hasFolioHint ? 'Facturado' : 'Sin Facturar';
  }
  if (invoiced <= 0 && !hasFolioHint) return 'Sin Facturar';
  if (invoiced > 0 && invoiced < itemsCount) return 'Facturado Parcial';
  if (invoiced >= itemsCount || (hasFolioHint && invoiced > 0)) return 'Facturado';
  if (hasFolioHint) return 'Facturado Parcial';
  return 'Sin Facturar';
};

export const resolvePoReceptionStatus = (order = {}) => {
  const inventoryCount = Number(order.inventory_items_count || 0);
  const qtyOrdered = Number(order.inventory_qty_ordered || 0);
  const qtyReceived = Number(order.inventory_qty_received || 0);
  const legacy = normalizePoStatus(order.status);

  if (legacy === 'Recepción No Conforme') return 'Recepción con Incidente';
  if (inventoryCount <= 0) return 'No Aplica';
  if (legacy === 'Recepción Conforme') return 'Recepción Conforme';

  if (qtyReceived <= 0) return 'Sin Recepción';
  if (qtyOrdered > 0 && qtyReceived + 1e-9 < qtyOrdered) return 'Recepción Parcial';
  if (qtyReceived > 0) return 'Recepción Conforme';
  return 'Sin Recepción';
};

export const resolvePoPaymentStatus = (order = {}, today = new Date()) => {
  const legacy = normalizePoStatus(order.status);
  if (isPoPaidStatus(legacy)) return 'Pago Completo';

  const advance = Number(order.advance_amount || 0);
  const amountDue =
    order.amount_due != null
      ? Number(order.amount_due)
      : Number(order.total_amount || 0) - advance - Number(order.withholdings_amount || 0);

  if (advance > 0 && amountDue > 0.01) return 'Pago Parcial';

  if (legacy === 'Por Pagar Atrasado') return 'Vencido';

  // Vencido por fecha de vencimiento calculada / factura.
  let due = null;
  if (order.dueIso || order.dueDate) {
    due = order.dueDate || null;
    if (!due && order.dueIso) {
      const parts = String(order.dueIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (parts) due = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    }
  } else if (order.invoice_due_date) {
    const raw = String(order.invoice_due_date).trim();
    const parts = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) due = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  if (due instanceof Date && !Number.isNaN(due.getTime())) {
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (due < todayLocal && amountDue > 0.01) return 'Vencido';
  }

  return 'Por Pagar';
};

/**
 * Resuelve las 4 columnas de ciclo de vida de una OC.
 */
export const resolvePoLifecycleStatuses = (order = {}, today = new Date()) => {
  const workflow_status = resolvePoWorkflowStatus(order);
  const invoicing_status = resolvePoInvoicingStatus(order);
  const reception_status = resolvePoReceptionStatus(order);
  const payment_status = resolvePoPaymentStatus(order, today);
  return {
    workflow_status,
    invoicing_status,
    reception_status,
    payment_status,
  };
};

export const getPoWorkflowStatusPresentation = (value, isDarkMode = false) =>
  presentationFromMaps(value || 'Borrador', PO_WORKFLOW_STATUS_COLORS, isDarkMode);

export const getPoInvoicingStatusPresentation = (value, isDarkMode = false) =>
  presentationFromMaps(value || 'Sin Facturar', PO_INVOICING_STATUS_COLORS, isDarkMode);

export const getPoReceptionStatusPresentation = (value, isDarkMode = false) =>
  presentationFromMaps(value || 'Sin Recepción', PO_RECEPTION_STATUS_COLORS, isDarkMode);

export const getPoPaymentStatusPresentation = (value, isDarkMode = false) =>
  presentationFromMaps(value || 'Por Pagar', PO_PAYMENT_STATUS_COLORS, isDarkMode);

export const getPoStatusPresentation = (statusRaw, isDarkMode = false) => {
  // Si ya viene un estado de flujo corto, usar paleta nueva.
  if (PO_WORKFLOW_STATUS_ORDER.includes(String(statusRaw || '').trim())) {
    return getPoWorkflowStatusPresentation(String(statusRaw).trim(), isDarkMode);
  }
  const normalized = normalizePoStatus(statusRaw);
  const label = String(statusRaw || '').trim() ? normalized : '—';
  const color = PO_STATUS_COLORS[normalized] || (isDarkMode ? '#71717a' : '#a8a29e');
  return {
    label,
    color,
    bar: color,
    bg: hexToRgba(color, isDarkMode ? 0.22 : 0.12),
    border: hexToRgba(color, isDarkMode ? 0.55 : 0.35),
  };
};

export const normalizePoItemType = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Sin tipo';
  const found = PO_ITEM_TYPE_ORDER.find((type) => type.toLowerCase() === raw.toLowerCase());
  return found || raw;
};

export const isClpLikeCurrency = (currency) => {
  const c = String(currency || 'CLP').trim().toUpperCase();
  return !c || c === 'CLP' || c === 'PESOS';
};

/** Monto de OC ya convertido a CLP en backend (total_amount). */
export const poAmountClp = (order) => {
  const total = Number(order?.total_amount || 0);
  return Number.isFinite(total) ? total : 0;
};

/**
 * Monto de línea en CLP.
 * Las líneas pueden estar en moneda del documento (formulario) o ya en CLP (importaciones).
 * Para evitar doble conversión, se reparte el total CLP de la OC en proporción al line_total.
 */
export const poLineAmountClp = (item, order = null) => {
  const line = Number(item?.line_total || 0);
  if (!Number.isFinite(line) || line <= 0) return 0;

  const poTotal = poAmountClp(order);
  const linesSum = Number(item?.po_lines_sum || 0);
  if (poTotal > 0 && linesSum > 0) {
    return Math.round(((line / linesSum) * poTotal) * 100) / 100;
  }

  if (isClpLikeCurrency(item?.currency || order?.currency)) return line;

  // Fallback sin cabecera: solo aplicar FX si el line_total parece moneda original.
  const rate = Number(item?.exchange_rate || order?.exchange_rate || 1) || 1;
  if (rate <= 1) return line;
  const converted = line * rate;
  // Si convertir dispara el monto a escala irreal vs el total de la OC, asumir CLP.
  if (poTotal > 0 && converted > poTotal * 20) return line;
  return Math.round(converted * 100) / 100;
};

export const formatSummaryCount = (value) => Number(value || 0).toLocaleString('es-CL');

export const formatSummaryMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0';
  return `$${Math.round(n).toLocaleString('es-CL')}`;
};

const toLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const addDays = (date, days) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + Number(days || 0));
  return next;
};

const toIsoDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Extrae días de crédito desde la condición de pago.
 * Contado / inmediato → 0; "Crédito 30 días" / "30 días" → 30.
 */
export const parsePaymentTermDays = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { days: 0, label: 'Sin condición', kind: 'unknown' };

  const folded = foldStatus(raw);
  if (
    folded.includes('contado') ||
    folded.includes('inmediato') ||
    folded.includes('cash') ||
    folded === '0' ||
    folded.includes('pago anticipado')
  ) {
    return { days: 0, label: raw, kind: 'cash' };
  }

  const creditMatch = folded.match(/(?:credito|credit|plazo)\s*(?:a\s*)?(\d+)/);
  if (creditMatch) {
    return { days: Number(creditMatch[1]), label: raw, kind: 'credit' };
  }

  const daysMatch = folded.match(/(\d+)\s*(?:d[iı]as?|days?)/);
  if (daysMatch) {
    return { days: Number(daysMatch[1]), label: raw, kind: 'credit' };
  }

  const bareNumber = folded.match(/^(\d+)$/);
  if (bareNumber) {
    return { days: Number(bareNumber[1]), label: raw, kind: 'credit' };
  }

  return { days: 0, label: raw, kind: 'unknown' };
};

/**
 * Fecha base del vencimiento: emisión de factura si existe; si no, fecha de la OC.
 */
export const resolvePoPaymentBaseDate = (order) => {
  const invoiceDate = toLocalDate(order?.invoice_issue_date);
  if (invoiceDate) {
    return { date: invoiceDate, source: 'invoice', iso: toIsoDate(invoiceDate) };
  }
  const orderDate = toLocalDate(order?.order_date);
  if (orderDate) {
    return { date: orderDate, source: 'order', iso: toIsoDate(orderDate) };
  }
  return { date: null, source: 'none', iso: '' };
};

/**
 * Vencimiento = fecha base + días de la condición de pago.
 */
export const resolvePoPaymentDueDate = (order, today = new Date()) => {
  const terms = parsePaymentTermDays(order?.payment_terms);
  const base = resolvePoPaymentBaseDate(order);
  const dueDate = base.date ? addDays(base.date, terms.days) : null;
  const todayLocal = toLocalDate(today) || new Date();
  const dueIso = dueDate ? toIsoDate(dueDate) : '';
  let bucket = 'sin_fecha';
  let daysUntilDue = null;
  if (dueDate) {
    daysUntilDue = Math.round((dueDate.getTime() - todayLocal.getTime()) / 86400000);
    if (isPoPaidStatus(order?.status)) bucket = 'pagado';
    else if (daysUntilDue < 0) bucket = 'vencido';
    else if (daysUntilDue === 0) bucket = 'hoy';
    else if (daysUntilDue <= 7) bucket = 'por_vencer';
    else bucket = 'futuro';
  } else if (isPoPaidStatus(order?.status)) {
    bucket = 'pagado';
  }

  return {
    ...base,
    dueDate,
    dueIso,
    paymentDays: terms.days,
    paymentTermsLabel: terms.label,
    paymentKind: terms.kind,
    daysUntilDue,
    bucket,
    invoiceFolios: String(order?.invoice_folios || '').trim(),
  };
};

/** OC relevantes para el calendario/proyección de pagos (excluye anuladas y respaldo). */
export const isPoPaymentScheduleCandidate = (order) => {
  const status = normalizePoStatus(order?.status);
  return status !== 'Respaldo' && status !== 'Anulada' && status !== 'Sin estado';
};

/**
 * Serie del gráfico de pagos: Pagadas vs proyección por estado de flujo.
 */
export const resolvePoPaymentChartGroup = (order = {}) => {
  const lifecycle = resolvePoLifecycleStatuses(order);
  if (lifecycle.payment_status === 'Pago Completo' || isPoPaidStatus(order.status)) {
    return 'Pagadas';
  }
  const workflow = lifecycle.workflow_status;
  if (workflow === 'Borrador' || workflow === 'Por Aprobar' || workflow === 'Emitida' || workflow === 'Aprobada') {
    return workflow;
  }
  return 'Aprobada';
};

export const PO_PAYMENT_CHART_GROUP_ORDER = [
  'Borrador',
  'Por Aprobar',
  'Emitida',
  'Aprobada',
  'Pagadas',
];

export const PO_PAYMENT_CHART_GROUP_COLORS = {
  Borrador: '#9ca3af',
  'Por Aprobar': '#7c3aed',
  Emitida: '#0ea5e9',
  Aprobada: '#f59e0b',
  Pagadas: '#15803d',
};

export const PO_PAYMENT_CHART_STACK_DIMENSION = {
  key: 'paymentGroup',
  label: 'Estado',
  order: PO_PAYMENT_CHART_GROUP_ORDER,
  colors: PO_PAYMENT_CHART_GROUP_COLORS,
  fallback: 'Aprobada',
};

export const buildPoPaymentScheduleRows = (orders = [], today = new Date()) =>
  (Array.isArray(orders) ? orders : [])
    .filter(isPoPaymentScheduleCandidate)
    .map((order) => {
      const schedule = resolvePoPaymentDueDate(order, today);
      const amount =
        order?.amount_due != null
          ? Number(order.amount_due)
          : poAmountClp(order) -
            Number(order?.advance_amount || 0) -
            Number(order?.withholdings_amount || 0);
      return {
        ...order,
        ...schedule,
        payment_amount: Number.isFinite(amount) ? amount : poAmountClp(order),
      };
    })
    .sort((a, b) => {
      if (!a.dueIso && !b.dueIso) return String(a.po_code || '').localeCompare(String(b.po_code || ''));
      if (!a.dueIso) return 1;
      if (!b.dueIso) return -1;
      if (a.dueIso !== b.dueIso) return a.dueIso.localeCompare(b.dueIso);
      return String(a.po_code || '').localeCompare(String(b.po_code || ''));
    });

